import {
  CRITICALITY_LABELS_UK,
  DEVICE_TYPE_LABELS_UK,
  DIAGRAM_GRID_SIZE,
  createNode,
  displayPortId,
  type ConnectionType,
  type Criticality,
  type DiagramDocument,
  type DiagramEdge,
  type DiagramNode,
  type DeviceType,
} from '@ukrop-nm/shared';

const CONNECTION_TYPE_UK: Record<ConnectionType, string> = {
  ethernet: 'Ethernet',
  fiber: 'Оптоволокно',
  wifi: 'Wi‑Fi',
  wan: 'WAN',
  sip: 'SIP / телефонія',
  logical: 'Логічне',
  unknown: 'Невідомо',
};

const EQUIPMENT_HEADERS = [
  'id',
  'monitorKey',
  'label',
  'type',
  'typeUk',
  'vendor',
  'model',
  'ip',
  'mac',
  'criticality',
  'location',
  'notes',
] as const;

const LINK_HEADERS = [
  'id',
  'monitorKey',
  'label',
  'sourceLabel',
  'sourcePort',
  'targetLabel',
  'targetPort',
  'connectionType',
  'networkCidr',
  'criticality',
] as const;

function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  if (/[;"\n\r]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}

function toCsv(headers: readonly string[], rows: string[][]): string {
  const lines = [
    headers.join(';'),
    ...rows.map((r) => headers.map((_, i) => csvCell(r[i])).join(';')),
  ];
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

export function equipmentCsv(doc: DiagramDocument): string {
  const devices = doc.nodes.filter((n) => n.type !== 'zone');
  const rows = devices.map((n) => {
    const inv = n.inventory ?? {};
    return [
      n.id,
      n.monitorKey,
      n.label,
      n.type,
      DEVICE_TYPE_LABELS_UK[n.type],
      inv.vendor ?? '',
      inv.model ?? '',
      inv.ipLocal ?? '',
      inv.macLocal ?? '',
      n.criticality,
      inv.location ?? '',
      n.notes ?? '',
    ];
  });
  return toCsv(EQUIPMENT_HEADERS, rows);
}

export function linksCsv(doc: DiagramDocument): string {
  const byId = new Map(doc.nodes.map((n) => [n.id, n]));
  const rows = doc.edges.map((e) => {
    const src = byId.get(e.sourceNodeId);
    const tgt = byId.get(e.targetNodeId);
    return [
      e.id,
      e.monitorKey ?? '',
      e.label ?? '',
      src?.label ?? '',
      displayPortId(e.sourcePort),
      tgt?.label ?? '',
      displayPortId(e.targetPort),
      e.connectionType,
      e.networkCidr ?? '',
      e.criticality,
    ];
  });
  return toCsv(LINK_HEADERS, rows);
}

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const raw = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const delim = raw.split('\n')[0]?.includes(';') ? ';' : ',';
  const rows: string[][] = [];
  let i = 0;
  const nextRow = (): string[] | null => {
    if (i >= raw.length) return null;
    const cells: string[] = [];
    let cell = '';
    let q = false;
    while (i <= raw.length) {
      const ch = raw[i];
      if (ch === undefined || (!q && (ch === '\n' || i === raw.length))) {
        if (ch === '\n') i += 1;
        cells.push(cell);
        return cells;
      }
      if (q) {
        if (ch === '"') {
          if (raw[i + 1] === '"') {
            cell += '"';
            i += 2;
            continue;
          }
          q = false;
          i += 1;
          continue;
        }
        cell += ch;
        i += 1;
        continue;
      }
      if (ch === '"') {
        q = true;
        i += 1;
        continue;
      }
      if (ch === delim) {
        cells.push(cell);
        cell = '';
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
    }
    return cells;
  };
  const headerRow = nextRow();
  if (!headerRow) return { headers: [], rows: [] };
  const headers = headerRow.map((h) => h.trim());
  while (i < raw.length) {
    const row = nextRow();
    if (!row) break;
    if (row.every((c) => !c.trim())) continue;
    rows.push(row);
  }
  return { headers, rows };
}

function col(headers: string[], row: string[], name: string): string {
  const idx = headers.findIndex((h) => h.trim().toLowerCase() === name.toLowerCase());
  return idx >= 0 ? (row[idx] ?? '').trim() : '';
}

function parseType(raw: string): DeviceType {
  const t = raw.trim();
  if (t in DEVICE_TYPE_LABELS_UK) return t as DeviceType;
  const hit = (Object.entries(DEVICE_TYPE_LABELS_UK) as Array<[DeviceType, string]>).find(
    ([, uk]) => uk.toLowerCase() === t.toLowerCase(),
  );
  return hit?.[0] ?? 'generic';
}

function parseCrit(raw: string): Criticality {
  const t = raw.trim().toLowerCase();
  const hit = (Object.entries(CRITICALITY_LABELS_UK) as Array<[Criticality, string]>).find(
    ([id, uk]) => id === t || uk.toLowerCase() === t,
  );
  return hit?.[0] ?? 'normal';
}

function parseConn(raw: string): ConnectionType {
  const t = raw.trim().toLowerCase();
  const hit = (Object.entries(CONNECTION_TYPE_UK) as Array<[ConnectionType, string]>).find(
    ([id, uk]) => id === t || uk.toLowerCase() === t,
  );
  return hit?.[0] ?? 'unknown';
}

function isEquipmentHeader(headers: string[]): boolean {
  const h = headers.map((x) => x.toLowerCase());
  return h.includes('type') || h.includes('typeuk');
}

function isLinkHeader(headers: string[]): boolean {
  const h = headers.map((x) => x.toLowerCase());
  return h.includes('sourcelabel') || h.includes('connectiontype') || h.includes('targetlabel');
}

function findNode(
  nodes: DiagramNode[],
  label: string,
  monitorKey: string,
): DiagramNode | undefined {
  if (monitorKey) {
    const byKey = nodes.find((n) => n.monitorKey === monitorKey);
    if (byKey) return byKey;
  }
  if (label) return nodes.find((n) => n.label === label);
  return undefined;
}

export type CsvImportResult = {
  document: DiagramDocument;
  warnings: string[];
};

/** Merge equipment/links CSV into a diagram (new nodes get a grid layout). */
export function applyCsvRegistries(
  base: DiagramDocument,
  files: Array<{ name: string; text: string }>,
): CsvImportResult {
  const warnings: string[] = [];
  const nodes = base.nodes.map((n) => structuredClone(n));
  const edges = base.edges.map((e) => structuredClone(e));
  let added = 0;
  const occupied = nodes.filter((n) => n.type !== 'zone').length;

  const place = (): { x: number; y: number } => {
    const i = occupied + added;
    added += 1;
    const colI = i % 8;
    const rowI = Math.floor(i / 8);
    return {
      x: DIAGRAM_GRID_SIZE * (6 + colI * 12),
      y: DIAGRAM_GRID_SIZE * (6 + rowI * 10),
    };
  };

  const parsed = files.map((file) => ({ file, ...parseCsv(file.text) }));

  for (const item of parsed) {
    const { file, headers, rows } = item;
    if (!headers.length) {
      warnings.push(`${file.name}: порожній CSV`);
      continue;
    }
    if (isLinkHeader(headers)) continue;
    if (!isEquipmentHeader(headers)) {
      warnings.push(`${file.name}: не розпізнано як реєстр обладнання чи ліній`);
      continue;
    }

    for (const row of rows) {
      const type = parseType(col(headers, row, 'type') || col(headers, row, 'typeUk'));
      if (type === 'zone') continue;
      const monitorKey = col(headers, row, 'monitorKey');
      const label = col(headers, row, 'label') || monitorKey;
      const existing = findNode(nodes, label, monitorKey);
      const inv = {
        vendor: col(headers, row, 'vendor') || undefined,
        model: col(headers, row, 'model') || undefined,
        ipLocal: col(headers, row, 'ip') || undefined,
        macLocal: col(headers, row, 'mac') || undefined,
        location: col(headers, row, 'location') || undefined,
      };
      if (existing) {
        existing.label = label || existing.label;
        existing.monitorKey = monitorKey || existing.monitorKey;
        const critRaw = col(headers, row, 'criticality');
        if (critRaw) existing.criticality = parseCrit(critRaw);
        existing.notes = col(headers, row, 'notes') || existing.notes;
        existing.inventory = { ...existing.inventory, ...inv };
        continue;
      }
      const id = col(headers, row, 'id') || crypto.randomUUID();
      const node = createNode(type, place(), id);
      node.label = label || node.label;
      if (monitorKey) node.monitorKey = monitorKey;
      const critRaw = col(headers, row, 'criticality');
      if (critRaw) node.criticality = parseCrit(critRaw);
      node.notes = col(headers, row, 'notes');
      node.inventory = inv;
      nodes.push(node);
    }
  }

  for (const item of parsed) {
    const { headers, rows } = item;
    if (!headers.length || !isLinkHeader(headers)) continue;
    for (const row of rows) {
      const sourceLabel = col(headers, row, 'sourceLabel');
      const targetLabel = col(headers, row, 'targetLabel');
      const src = findNode(nodes, sourceLabel, col(headers, row, 'sourceKey'));
      const tgt = findNode(nodes, targetLabel, col(headers, row, 'targetKey'));
      if (!src || !tgt) {
        warnings.push(`Лінія без кінців: ${sourceLabel} → ${targetLabel}`);
        continue;
      }
      const id = col(headers, row, 'id') || crypto.randomUUID();
      if (edges.some((e) => e.id === id)) continue;
      const edge: DiagramEdge = {
        id,
        sourceNodeId: src.id,
        targetNodeId: tgt.id,
        sourcePort: col(headers, row, 'sourcePort') || undefined,
        targetPort: col(headers, row, 'targetPort') || undefined,
        connectionType: parseConn(col(headers, row, 'connectionType')),
        monitorSource: 'manual',
        monitorKey: col(headers, row, 'monitorKey') || undefined,
        label: col(headers, row, 'label') || undefined,
        networkCidr: col(headers, row, 'networkCidr') || undefined,
        criticality: parseCrit(col(headers, row, 'criticality')),
      };
      edges.push(edge);
    }
  }

  return {
    document: {
      ...base,
      nodes,
      edges,
      updatedAt: new Date().toISOString(),
    },
    warnings,
  };
}
